import type { Lang } from "./commentary.js";

/**
 * Live speech, in the same voice as the recorded clips.
 *
 * The pre-rendered bank covers everything that never changes — every way of
 * describing a four, a wicket, a dot. What it cannot cover is anything with a
 * player name or a score in it: the call at the end of an over, the target, the
 * result. Those changed every match, so they fell back to the phone's own
 * text-to-speech, and the join between a recorded line and a robotic one was
 * audible and jarring.
 *
 * kokoro-js runs the same model the clips were rendered with, in the browser.
 * So the over call is spoken by the same voice that just described the ball.
 *
 * The cost is honest and worth stating: about 82 MB on first use. It is fetched
 * once, cached by the browser, and only when the user asks for it — never on a
 * page load, and never on a metered connection they did not choose.
 */

type TTS = {
  generate(text: string, opts: { voice: string }): Promise<{ toBlob(): Blob }>;
};

export type State = "absent" | "loading" | "ready" | "failed";

let tts: TTS | null = null;
let state: State = "absent";
let loading: Promise<TTS | null> | null = null;
let listeners: ((s: State) => void)[] = [];

/**
 * Which kokoro voice each recorded pack was rendered with.
 *
 * The live model has to match whichever pack is selected, or the score at the
 * end of an over arrives in a different person's voice from the ball that
 * preceded it — which is the whole problem this was built to solve.
 */
const PACK_VOICE: Record<string, string> = {
  "en": "bm_george",
  "en-daniel": "bm_daniel",
  "hi": "hm_omega",
  "hi-alpha": "hf_alpha",
};

export function getState(): State { return state; }

export function onState(fn: (s: State) => void): () => void {
  listeners.push(fn);
  return () => { listeners = listeners.filter((f) => f !== fn); };
}

function setState(s: State): void {
  state = s;
  for (const fn of listeners) fn(s);
}

/** WebGPU is far quicker; WASM works everywhere and is the fallback. */
async function hasWebGPU(): Promise<boolean> {
  const nav = navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } };
  if (!nav.gpu) return false;
  try { return !!(await nav.gpu.requestAdapter()); } catch { return false; }
}

/**
 * Fetch and start the model. Must be triggered by an explicit user action —
 * 82 MB is not something to download on someone's behalf.
 */
export async function enable(): Promise<boolean> {
  if (tts) return true;
  if (loading) return !!(await loading);

  setState("loading");
  loading = (async () => {
    try {
      const { KokoroTTS } = await import("kokoro-js");
      const webgpu = await hasWebGPU();
      const model = await KokoroTTS.from_pretrained(
        "onnx-community/Kokoro-82M-v1.0-ONNX",
        webgpu
          ? { dtype: "fp32", device: "webgpu" }
          // q8 on WASM: a quarter the download and the difference is slight
          // against a phone speaker at a ground.
          : { dtype: "q8", device: "wasm" },
      );
      tts = model as unknown as TTS;
      setState("ready");
      return tts;
    } catch (e) {
      console.error("kokoro: could not start", String(e));
      setState("failed");
      return null;
    } finally {
      loading = null;
    }
  })();
  return !!(await loading);
}

let el: HTMLAudioElement | null = null;
let busy = false;

/**
 * Speak a line that no clip can cover.
 *
 * Returns false when the model is not running, so the caller can fall back to
 * the device voice rather than the line simply going unsaid.
 */
export async function say(text: string, lang: Lang, pack?: string): Promise<boolean> {
  if (!tts || busy) return false;
  busy = true;
  try {
    const voice = PACK_VOICE[pack ?? lang] ?? PACK_VOICE[lang] ?? "bm_george";
    const audio = await tts.generate(text, { voice });
    const url = URL.createObjectURL(audio.toBlob());
    if (!el) el = new Audio();
    el.src = url;
    await el.play().catch(() => undefined);
    // Free the blob once it has finished rather than holding every line of a
    // match in memory.
    el.onended = () => URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    console.error("kokoro: synthesis failed", String(e));
    return false;
  } finally {
    busy = false;
  }
}

export function stop(): void {
  if (el) { el.pause(); el.currentTime = 0; }
  busy = false;
}
